import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { FSProject } from "../../Types/FinishingSchedule/FinishingSchedule";
import { FSAppliance } from "../../Types/FinishingSchedule/Appliance";
import { FSBrand } from "../../Types/FinishingSchedule/Brand";
import { FSColor } from "../../Types/FinishingSchedule/Color";
import { FSDocument } from "../../Types/FinishingSchedule/Document";
import { FSItem } from "../../Types/FinishingSchedule/Item";
import { FSLocation } from "../../Types/FinishingSchedule/Location";
import { FSSize } from "../../Types/FinishingSchedule/Size";
import { FSType } from "../../Types/FinishingSchedule/Type";
import { FSPaint } from "../../Types/FinishingSchedule/Paint/Paint";
import { FSAccessoryName } from "../../Types/FinishingSchedule/Paint/AccessoryName";
import { FSAccessorySubName } from "../../Types/FinishingSchedule/Paint/AccessorySubName";
import { FSFloorFinishing } from "../../Types/FinishingSchedule/FloorFinishing/FloorFinishing";
import { FSTile } from "../../Types/FinishingSchedule/Tile/Tile";
import { FSTileShop } from "../../Types/FinishingSchedule/Tile/TileShop";
import { FSDoor } from "../../Types/FinishingSchedule/Door/Door";
import { FSDoorSize } from "../../Types/FinishingSchedule/Door/DoorSize";

import {
    fetchFSProjectList,
    fetchFSApplianceList,
    fetchFSBrandList,
    fetchFSColorList,
    fetchFSDocumentList,
    fetchFSItemList,
    fetchFSLocationList,
    fetchFSSizeList,
    fetchFSTypeList,
    fetchFSPaintList,
    fetchFSAccessoryNameList,
    fetchFSAccessorySubNameList,
    fetchFSFloorFinishingList,
    fetchFSTileList,
    fetchFSTileShopList,
    fetchFSDoorList,
    fetchFSDoorSizeList
} from "../../Thunks/FinishingScheduleThunks";

interface FinishingScheduleSliceState {
    loadingFSProjects: boolean;
    fsProjects: FSProject[];
    errorFSProjects: string | null;

    loadingFSAppliances: boolean;
    fsAppliances: FSAppliance[];
    errorFSAppliances: string | null;

    loadingFSBrands: boolean;
    fsBrands: FSBrand[];
    errorFSBrands: string | null;

    loadingFSColors: boolean;
    fsColors: FSColor[];
    errorFSColors: string | null;

    loadingFSDocuments: boolean;
    fsDocuments: FSDocument[];
    errorFSDocuments: string | null;

    loadingFSItems: boolean;
    fsItems: FSItem[];
    errorFSItems: string | null;

    loadingFSLocations: boolean;
    fsLocations: FSLocation[];
    errorFSLocations: string | null;

    loadingFSSizes: boolean;
    fsSizes: FSSize[];
    errorFSSizes: string | null;

    loadingFSTypes: boolean;
    fsTypes: FSType[];
    errorFSTypes: string | null;

    loadingFSPaints: boolean;
    fsPaints: FSPaint[];
    errorFSPaints: string | null;

    loadingFSAccessoryNames: boolean;
    fsAccessoryNames: FSAccessoryName[];
    errorFSAccessoryNames: string | null;

    loadingFSAccessorySubNames: boolean;
    fsAccessorySubNames: FSAccessorySubName[];
    errorFSAccessorySubNames: string | null;

    loadingFSFloorFinishings: boolean;
    fsFloorFinishings: FSFloorFinishing[];
    errorFSFloorFinishings: string | null;

    loadingFSTiles: boolean;
    fsTiles: FSTile[];
    errorFSTiles: string | null;

    loadingFSTileShops: boolean;
    fsTileShops: FSTileShop[];
    errorFSTileShops: string | null;

    loadingFSDoors: boolean;
    fsDoors: FSDoor[];
    errorFSDoors: string | null;

    loadingFSDoorSizes: boolean;
    fsDoorSizes: FSDoorSize[];
    errorFSDoorSizes: string | null;
}

// set inital valus
const initialState: FinishingScheduleSliceState = {
    loadingFSProjects: false,
    fsProjects: [],
    errorFSProjects: '',

    loadingFSAppliances: false,
    fsAppliances: [],
    errorFSAppliances: '',

    loadingFSBrands: false,
    fsBrands: [],
    errorFSBrands: '',

    loadingFSColors: false,
    fsColors: [],
    errorFSColors: '',

    loadingFSDocuments: false,
    fsDocuments: [],
    errorFSDocuments: '',

    loadingFSItems: false,
    fsItems: [],
    errorFSItems: '',

    loadingFSLocations: false,
    fsLocations: [],
    errorFSLocations: '',

    loadingFSSizes: false,
    fsSizes: [],
    errorFSSizes: '',

    loadingFSTypes: false,
    fsTypes: [],
    errorFSTypes: '',

    loadingFSPaints: false,
    fsPaints: [],
    errorFSPaints: '',

    loadingFSAccessoryNames: false,
    fsAccessoryNames: [],
    errorFSAccessoryNames: '',

    loadingFSAccessorySubNames: false,
    fsAccessorySubNames: [],
    errorFSAccessorySubNames: '',

    loadingFSFloorFinishings: false,
    fsFloorFinishings: [],
    errorFSFloorFinishings: '',

    loadingFSTiles: false,
    fsTiles: [],
    errorFSTiles: '',

    loadingFSTileShops: false,
    fsTileShops: [],
    errorFSTileShops: '',

    loadingFSDoors: false,
    fsDoors: [],
    errorFSDoors: '',

    loadingFSDoorSizes: false,
    fsDoorSizes: [],
    errorFSDoorSizes: '',
}

// set state value for api response
const finishingScheduleSlice = createSlice({
    name: 'finishingSchedule',
    initialState,
    reducers: {
    },
    extraReducers(builder) {
        builder
            /* Finishing shedule projects */
            .addCase(fetchFSProjectList.pending, state => {
                state.loadingFSProjects = true;
                state.errorFSProjects = null;
            })
            .addCase(fetchFSProjectList.fulfilled, (state, action: PayloadAction<FSProject[]>) => {
                state.loadingFSProjects = false;
                state.fsProjects = action.payload;
            })
            .addCase(fetchFSProjectList.rejected, (state, action) => {
                state.loadingFSProjects = false;
                state.errorFSProjects = action.error.message ?? "Failed to fetch projects.";
            })

            /* Finishing shedule appliances */
            .addCase(fetchFSApplianceList.pending, state => {
                state.loadingFSAppliances = true;
                state.errorFSAppliances = null;
            })
            .addCase(fetchFSApplianceList.fulfilled, (state, action: PayloadAction<FSAppliance[]>) => {
                state.loadingFSAppliances = false;
                state.fsAppliances = action.payload;
            })
            .addCase(fetchFSApplianceList.rejected, (state, action) => {
                state.loadingFSAppliances = false;
                state.errorFSAppliances = action.error.message ?? "Failed to fetch appliances.";
            })

            /* Finishing shedule brands */
            .addCase(fetchFSBrandList.pending, state => {
                state.loadingFSBrands = true;
                state.errorFSBrands = null;
            })
            .addCase(fetchFSBrandList.fulfilled, (state, action: PayloadAction<FSBrand[]>) => {
                state.loadingFSBrands = false;
                state.fsBrands = action.payload;
            })
            .addCase(fetchFSBrandList.rejected, (state, action) => {
                state.loadingFSBrands = false;
                state.errorFSBrands = action.error.message ?? "Failed to fetch brands.";
            })

            /* Finishing shedule colors */
            .addCase(fetchFSColorList.pending, state => {
                state.loadingFSColors = true;
                state.errorFSColors = null;
            })
            .addCase(fetchFSColorList.fulfilled, (state, action: PayloadAction<FSColor[]>) => {
                state.loadingFSColors = false;
                state.fsColors = action.payload;
            })
            .addCase(fetchFSColorList.rejected, (state, action) => {
                state.loadingFSColors = false;
                state.errorFSColors = action.error.message ?? "Failed to fetch colors.";
            })

            /* Finishing shedule documents */
            .addCase(fetchFSDocumentList.pending, state => {
                state.loadingFSDocuments = true;
                state.errorFSDocuments = null;
            })
            .addCase(fetchFSDocumentList.fulfilled, (state, action: PayloadAction<FSDocument[]>) => {
                state.loadingFSDocuments = false;
                state.fsDocuments = action.payload;
            })
            .addCase(fetchFSDocumentList.rejected, (state, action) => {
                state.loadingFSDocuments = false;
                state.errorFSDocuments = action.error.message ?? "Failed to fetch documents.";
            })

            /* Finishing shedule items */
            .addCase(fetchFSItemList.pending, state => {
                state.loadingFSItems = true;
                state.errorFSItems = null;
            })
            .addCase(fetchFSItemList.fulfilled, (state, action: PayloadAction<FSItem[]>) => {
                state.loadingFSItems = false;
                state.fsItems = action.payload;
            })
            .addCase(fetchFSItemList.rejected, (state, action) => {
                state.loadingFSItems = false;
                state.errorFSItems = action.error.message ?? "Failed to fetch items.";
            })

            /* Finishing shedule locations */
            .addCase(fetchFSLocationList.pending, state => {
                state.loadingFSLocations = true;
                state.errorFSLocations = null;
            })
            .addCase(fetchFSLocationList.fulfilled, (state, action: PayloadAction<FSLocation[]>) => {
                state.loadingFSLocations = false;
                state.fsLocations = action.payload;
            })
            .addCase(fetchFSLocationList.rejected, (state, action) => {
                state.loadingFSLocations = false;
                state.errorFSLocations = action.error.message ?? "Failed to fetch locations.";
            })

            /* Finishing shedule sizes */
            .addCase(fetchFSSizeList.pending, state => {
                state.loadingFSSizes = true;
                state.errorFSSizes = null;
            })
            .addCase(fetchFSSizeList.fulfilled, (state, action: PayloadAction<FSSize[]>) => {
                state.loadingFSSizes = false;
                state.fsSizes = action.payload;
            })
            .addCase(fetchFSSizeList.rejected, (state, action) => {
                state.loadingFSSizes = false;
                state.errorFSSizes = action.error.message ?? "Failed to fetch sizes.";
            })

            /* Finishing shedule types */
            .addCase(fetchFSTypeList.pending, state => {
                state.loadingFSTypes = true;
                state.errorFSTypes = null;
            })
            .addCase(fetchFSTypeList.fulfilled, (state, action: PayloadAction<FSType[]>) => {
                state.loadingFSTypes = false;
                state.fsTypes = action.payload;
            })
            .addCase(fetchFSTypeList.rejected, (state, action) => {
                state.loadingFSTypes = false;
                state.errorFSTypes = action.error.message ?? "Failed to fetch types.";
            })

            /* Finishing shedule paints */
            .addCase(fetchFSPaintList.pending, state => {
                state.loadingFSPaints = true;
                state.errorFSPaints = null;
            })
            .addCase(fetchFSPaintList.fulfilled, (state, action: PayloadAction<FSPaint[]>) => {
                state.loadingFSPaints = false;
                state.fsPaints = action.payload;
            })
            .addCase(fetchFSPaintList.rejected, (state, action) => {
                state.loadingFSPaints = false;
                state.errorFSPaints = action.error.message ?? "Failed to fetch paints.";
            })

            /* Finishing shedule accessory names */
            .addCase(fetchFSAccessoryNameList.pending, state => {
                state.loadingFSAccessoryNames = true;
                state.errorFSAccessoryNames = null;
            })
            .addCase(fetchFSAccessoryNameList.fulfilled, (state, action: PayloadAction<FSAccessoryName[]>) => {
                state.loadingFSAccessoryNames = false;
                state.fsAccessoryNames = action.payload;
            })
            .addCase(fetchFSAccessoryNameList.rejected, (state, action) => {
                state.loadingFSAccessoryNames = false;
                state.errorFSAccessoryNames = action.error.message ?? "Failed to fetch accessory names.";
            })

            /* Finishing shedule accessory sub names */
            .addCase(fetchFSAccessorySubNameList.pending, state => {
                state.loadingFSAccessorySubNames = true;
                state.errorFSAccessorySubNames = null;
            })
            .addCase(fetchFSAccessorySubNameList.fulfilled, (state, action: PayloadAction<FSAccessorySubName[]>) => {
                state.loadingFSAccessorySubNames = false;
                state.fsAccessorySubNames = action.payload;
            })
            .addCase(fetchFSAccessorySubNameList.rejected, (state, action) => {
                state.loadingFSAccessorySubNames = false;
                state.errorFSAccessorySubNames = action.error.message ?? "Failed to fetch accessory sub names.";
            })

            /* Finishing shedule floor finishings */
            .addCase(fetchFSFloorFinishingList.pending, state => {
                state.loadingFSFloorFinishings = true;
                state.errorFSFloorFinishings = null;
            })
            .addCase(fetchFSFloorFinishingList.fulfilled, (state, action: PayloadAction<FSFloorFinishing[]>) => {
                state.loadingFSFloorFinishings = false;
                state.fsFloorFinishings = action.payload;
            })
            .addCase(fetchFSFloorFinishingList.rejected, (state, action) => {
                state.loadingFSFloorFinishings = false;
                state.errorFSFloorFinishings = action.error.message ?? "Failed to fetch floor finishings.";
            })

            /* Finishing shedule tiles */
            .addCase(fetchFSTileList.pending, state => {
                state.loadingFSTiles = true;
                state.errorFSTiles = null;
            })
            .addCase(fetchFSTileList.fulfilled, (state, action: PayloadAction<FSTile[]>) => {
                state.loadingFSTiles = false;
                state.fsTiles = action.payload;
            })
            .addCase(fetchFSTileList.rejected, (state, action) => {
                state.loadingFSTiles = false;
                state.errorFSTiles = action.error.message ?? "Failed to fetch tiles.";
            })

            /* Finishing shedule shops */
            .addCase(fetchFSTileShopList.pending, state => {
                state.loadingFSTileShops = true;
                state.errorFSTileShops = null;
            })
            .addCase(fetchFSTileShopList.fulfilled, (state, action: PayloadAction<FSTileShop[]>) => {
                state.loadingFSTileShops = false;
                state.fsTileShops = action.payload;
            })
            .addCase(fetchFSTileShopList.rejected, (state, action) => {
                state.loadingFSTileShops = false;
                state.errorFSTileShops = action.error.message ?? "Failed to fetch shops.";
            })

            /* Finishing shedule doors */
            .addCase(fetchFSDoorList.pending, state => {
                state.loadingFSDoors = true;
                state.errorFSDoors = null;
            })
            .addCase(fetchFSDoorList.fulfilled, (state, action: PayloadAction<FSDoor[]>) => {
                state.loadingFSDoors = false;
                state.fsDoors = action.payload;
            })
            .addCase(fetchFSDoorList.rejected, (state, action) => {
                state.loadingFSDoors = false;
                state.errorFSDoors = action.error.message ?? "Failed to fetch doors.";
            })

            /* Finishing shedule door sizes */
            .addCase(fetchFSDoorSizeList.pending, state => {
                state.loadingFSDoorSizes = true;
                state.errorFSDoorSizes = null;
            })
            .addCase(fetchFSDoorSizeList.fulfilled, (state, action: PayloadAction<FSDoorSize[]>) => {
                state.loadingFSDoorSizes = false;
                state.fsDoorSizes = action.payload;
            })
            .addCase(fetchFSDoorSizeList.rejected, (state, action) => {
                state.loadingFSDoorSizes = false;
                state.errorFSDoorSizes = action.error.message ?? "Failed to fetch door sizes.";
            })
    }
})

export default finishingScheduleSlice.reducer